import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useGroupData, addGroupEvent, deleteGroupEvent } from '../../hooks/useGroups';
import Modal from '../../components/ui/Modal';
import { fmtDate } from '../../lib/constants';

export default function GroupEvents() {
  const { profile } = useAuth();
  const { group, events, loading, refreshEvents } = useGroupData(profile?.group_id);
  const [showAdd, setShowAdd] = useState(false);
  const [form,    setForm]    = useState({ title: '', date: '', time: '', location: '', description: '' });
  const [saving,  setSaving]  = useState(false);

  const now      = new Date();
  const upcoming = events.filter(e => new Date(e.date) >= new Date(now.toDateString())).sort((a, b) => new Date(a.date) - new Date(b.date));
  const past     = events.filter(e => new Date(e.date) <  new Date(now.toDateString())).sort((a, b) => new Date(b.date) - new Date(a.date));

  async function handleAdd(e) {
    e.preventDefault(); setSaving(true);
    const ev = await addGroupEvent({ ...form, group_id: profile.group_id });
    if (ev) { setShowAdd(false); setForm({ title: '', date: '', time: '', location: '', description: '' }); refreshEvents(); }
    setSaving(false);
  }

  async function handleDelete(id) {
    if (!window.confirm('Remove this event?')) return;
    await deleteGroupEvent(id);
    refreshEvents();
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Loading…</div>;

  return (
    <div>
      <div className="flex-between mb-4">
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800 }}>📅 {group?.name} Calendar</h1>
          <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 2 }}>{upcoming.length} upcoming · {past.length} past events</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Add Event</button>
      </div>

      {/* Upcoming */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .6, marginBottom: 12 }}>Upcoming Events ({upcoming.length})</div>
        {upcoming.length === 0 && <div className="alert alert-info">No upcoming events. Add one to get started!</div>}
        {upcoming.map(e => (
          <div key={e.id} className="card mb-3" style={{ padding: '16px 20px', display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <div style={{ background: 'var(--red-dark)', borderRadius: 10, padding: '10px 14px', textAlign: 'center', flexShrink: 0, minWidth: 52 }}>
              <div style={{ color: 'var(--gold-light, #FFD54F)', fontSize: 10, fontWeight: 700, letterSpacing: 1 }}>
                {new Date(e.date).toLocaleString('default', { month: 'short' }).toUpperCase()}
              </div>
              <div style={{ color: 'white', fontSize: 22, fontWeight: 900, lineHeight: 1.1 }}>{new Date(e.date).getDate()}</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)', marginBottom: 4 }}>{e.title}</div>
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                {e.time && `🕐 ${e.time}`}{e.time && e.location ? '  ·  ' : ''}
                {e.location && `📍 ${e.location}`}
              </div>
              {e.description && <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 6 }}>{e.description}</div>}
            </div>
            <button className="btn btn-danger btn-sm" onClick={() => handleDelete(e.id)}>Remove</button>
          </div>
        ))}
      </div>

      {/* Past */}
      {past.length > 0 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .6, marginBottom: 12 }}>Past Events ({past.length})</div>
          {past.map(e => (
            <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border)', opacity: .6 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text2)' }}>{e.title}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>{fmtDate(e.date)}{e.location ? ` · 📍 ${e.location}` : ''}</div>
              </div>
              <button className="btn btn-ghost btn-sm" style={{ color: '#C62828' }} onClick={() => handleDelete(e.id)}>Remove</button>
            </div>
          ))}
        </div>
      )}

      {/* Add Modal */}
      {showAdd && (
        <Modal title="Add Event" onClose={() => setShowAdd(false)}
          footer={<><button className="btn btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button><button className="btn btn-primary" form="event-form" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Add Event'}</button></>}>
          <form id="event-form" onSubmit={handleAdd}>
            <div className="form-group"><label className="form-label">Title *</label><input className="form-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required /></div>
            <div className="grid-2">
              <div className="form-group"><label className="form-label">Date *</label><input className="form-input" type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} required /></div>
              <div className="form-group"><label className="form-label">Time</label><input className="form-input" type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))} /></div>
            </div>
            <div className="form-group"><label className="form-label">Location</label><input className="form-input" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="e.g. Church Hall" /></div>
            <div className="form-group"><label className="form-label">Description</label><textarea className="form-textarea" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} /></div>
          </form>
        </Modal>
      )}
    </div>
  );
}
